import { Injectable } from "@angular/core";
import { delay, firstValueFrom, of, retry, switchMap, throwError } from "rxjs";
import { State } from "../address/State";
import { ReferenceFileUploadResponse } from "../files/ReferenceFileUploadResponse";
import { ReferenceFileData } from "./ReferenceFileData";
import { ReferenceFileDetail } from "./ReferenceFileDetail";
import { Logger } from "../../infrastructure/logging/logger";
import { UserContextProvider } from "../../infrastructure/context/user-context-provider";
import { PortalBeHttpClient } from "../../infrastructure/http/legacy/PortalBeHttpClient";
import { AdminPortalAPIHttpClient } from "../../infrastructure/http/v2/AdminPortalAPIHttpClient";
import { getStates, getOrganizationAddressStates } from "../../utilities/AddressUtility";
import { ImageUtility } from "../../utilities/ImageUtility";

@Injectable({ providedIn: 'root' })
export class ReferenceService {

    constructor(
        private adminPortalApiHttpClient: AdminPortalAPIHttpClient,
        private readonly portalBeHttpClient: PortalBeHttpClient,
        private readonly userContextProvider: UserContextProvider,
        private readonly logService: Logger,
        private readonly imageUtility: ImageUtility
    ) { }

    async validatePropertyReferenceIds(referenceIds: any): Promise<any> {
        try {
            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.post('/properties/validate/id/reference', { ...referenceIds}));
            if (!response) {
                return Promise.reject(`Validating property reference IDs response null`);
            }
            return Promise.resolve(response.referenceIds);
        } catch (e) {
            return Promise.reject(`Error while validating property reference IDs: ${e}`);
        }
    }

    getFileById = async (fileId: number): Promise<ReferenceFileDetail> => {
        try {
            const response: any = await firstValueFrom(this.adminPortalApiHttpClient.get(`/files/${fileId}`));
            if(!response) {
                throw new Error(`Get file response null`);
            }

            return Promise.resolve(response.data);
        } catch(e) {
            this.logService.handleError(e, `Error retrieving File Data by File ID: ${fileId}`, `Error retrieving File Data by File ID: ${fileId}`);
            
            throw e;
        }
    }

    getFileDataById = async (fileId: number | string): Promise<ReferenceFileData> => {
        try {
            const headers = {
                'Authorization': `Bearer ${this.userContextProvider.token}`
            }

            const response: any = await firstValueFrom(this.portalBeHttpClient.get(`/files/download`, { params: { id: fileId } }, 'json', headers));
            const fileData = await this.imageUtility.base64ToBlob(response?.data?.base64Url);
            return Promise.resolve({
                fileId: fileId,
                image: fileData
            });
        } catch(e) {
            this.logService.handleError(e, `Error retrieving File Data by File ID: ${fileId}`, `Error retrieving File Data by File ID: ${fileId}`);

            throw e;
        }
    }

    downloadFile = (fileId: number): void => {
        const token = this.userContextProvider.account?.token;
        const url = `${this.portalBeHttpClient.getBaseApiUrl()}/files/download?id=${fileId}}&bearer_token=${token}`;
        window.open(url, '_blank');
    }

    uploadFile = async (organizationId: string, fileKey: string, fileName: string, fileType: string, fileSize: number, fileData: string, fileDescription?: string): Promise<ReferenceFileUploadResponse> => {
        try {
            const formData = new FormData();
            formData.append("file_name", fileName); 
            formData.append("file_type", fileType);
            formData.append("file_size", fileSize.toString());
            formData.append("file_data", fileData);
            formData.append("file_key", fileKey);
            formData.append("organization_id", organizationId);

            const headers = {
                'Authorization': `Bearer ${this.userContextProvider.token}`
            }

            const response: any = await firstValueFrom(
                this.portalBeHttpClient.post('/files/upload', formData, headers).pipe(
                    switchMap((response: any) => {
                        if(!response.body?.data?.id) {
                            return throwError(() => new Error('Upload failed: No file ID in response'));
                        }
                        return of(response);
                    }),
                    retry({
                        count: 3,
                        delay: (error: any, retryCount: number) => {
                            if(retryCount < 3) {
                                const exponentialDelay = Math.pow(2, retryCount) * 1000;
                                return of(error).pipe(delay(exponentialDelay));
                            }
                            return throwError(() => error);
                        }
                    }),
                )
            );

            return {
                fileName: fileName,
                fileId: response?.body?.data?.id,
                error: !response?.body?.data || response.status != 200,
                description: fileDescription
            };
        } catch(e) {
            this.logService.handleError(e, `Error while uploading File Name: ${fileName} of Type: ${fileType}`, `Error while uploading File Name: ${fileName} of Type: ${fileType}`);

            return {
                fileName: fileName,
                error: true
            }
        }
    }

    getAllStates = (): State[] => {
        return getStates();
    }

    getAllOrganizationAddressStates = (): State[] => {
        return getOrganizationAddressStates();
    }
}